import React, { lazy, Suspense } from 'react';
import {
  Route,
  Routes,
  useLocation,
  Outlet,
  Navigate,
  useParams,
} from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from '@mui/material';

const Home = lazy(() => import('../features/home/pages/home'));
const Compare = lazy(() => import('../pages/comparison'));
const About = lazy(() => import('../pages/about'));
const Gallery = lazy(() => import('../features/gallery/pages/front/gallery'));
const Confirmation = lazy(
  () => import('../features/checkout/pages/confirmation/confirmation')
);
const AccountLogin = lazy(
  () => import('../features/accountLogin/pages/accountLogin/accountLogin')
);
const WelcomePage = lazy(
  () => import('../features/accountLogin/pages/welcomePage/welcomePage')
);
const AccountDashboard = lazy(
  () => import('../features/accountDashboard/pages/accountDashboard')
);
const HistoryGallery2 = lazy(
  () => import('../features/gallery/pages/history/historyGallery2')
);
const FlowersGallery = lazy(
  () => import('../features/gallery/pages/flowers/flowersGallery')
);
const Contact = lazy(() => import('../pages/contact'));
const ProductDetails = lazy(() => import('../pages/productDetails'));
const LoginLanding = lazy(
  () => import('../features/accountLogin/pages/loginLanding/loginLanding')
);
const ControlBoard = lazy(() => import('../features/admin/pages/controlBoard'));
const ShippingPolicy = lazy(() => import('../pages/shippingPolicy'));
const ReturnPolicy = lazy(() => import('../pages/returnPolicy'));
const Extract = lazy(() => import('../pages/extract2'));
const CustomCheckout = lazy(
  () => import('../features/checkout/pages/customCheckout/customCheckout')
);
const NotFound = lazy(() => import('../pages/404'));

/** Old product URLs: /item/:slug → /products/:slug (hosting should also 301; see docs/). */
const RedirectItemSlugToProducts = () => {
  const { slug } = useParams();
  return <Navigate to={`/products/${slug}`} replace />;
};

const Circular = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 40,
        height: '100vh',
      }}
    >
      <CircularProgress color="secondary" />
    </Box>
  );
};

const SuspenseLayout = () => (
  <Suspense fallback={<Circular />}>
    <Outlet />
  </Suspense>
);

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes key={location.pathname} location={location}>
        <Route element={<SuspenseLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/gallery" element={<Gallery />} exact />
          <Route path="/contact" element={<Contact />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/gallery/history2" element={<HistoryGallery2 />} />
          <Route path="/gallery/flowers" element={<FlowersGallery />} />
          <Route path="/admincontrolboard" element={<ControlBoard />} exact />
          <Route path="/products/:slug" element={<ProductDetails />} />
          <Route path="/item/:slug" element={<RedirectItemSlugToProducts />} />
          <Route path="/checkout/success" element={<Confirmation />} />
          <Route path="/checkout/payment" element={<CustomCheckout />} />
          <Route path="/accountlogin" element={<AccountLogin />} />
          <Route path="/accountlogin/success" element={<LoginLanding />} />
          <Route path="/welcomepage" element={<WelcomePage />} />
          <Route path="/accountdashboard" element={<AccountDashboard />} />
          <Route path="/shippingpolicy" element={<ShippingPolicy />} />
          <Route path="/returnpolicy" element={<ReturnPolicy />} />
          <Route path="/vanillaextract" element={<Extract />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </AnimatePresence>
  );
};

export default AnimatedRoutes;
